import { useEffect } from "react";

/**
 * Desktop full-page scrolling: one wheel notch / arrow / swipe = exactly one
 * section, snapped to fill the viewport. A tracked target index (not scrollY)
 * drives navigation, so consecutive gestures advance reliably even while the
 * previous smooth-scroll is still animating.
 *
 * Below 1024px it does nothing — native scroll. FAQ and footer scroll within
 * themselves when they overflow, then advance once their edge is reached.
 */
export default function FullpageScroll() {
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    let locked = false;
    let lockTimer = null;
    let idx = 0; // authoritative current/target section index

    const targets = () =>
      Array.from(document.querySelectorAll("#top > section, #top > footer"));

    const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;

    // Sync idx from scroll position (used on mount + manual scrolls / resizes).
    const syncFromScroll = () => {
      const els = targets();
      const y = window.scrollY + 4;
      let i = 0;
      els.forEach((el, k) => {
        if (docTop(el) <= y) i = k;
      });
      idx = i;
    };

    const go = (dir) => {
      const els = targets();
      const next = Math.max(0, Math.min(els.length - 1, idx + dir));
      if (next === idx) return; // already at top/bottom — let user feel the edge
      idx = next;
      locked = true;
      els[next].scrollIntoView({ behavior: "smooth", block: "start" });
      clearTimeout(lockTimer);
      // Long enough to cover Chromium's smooth-scroll for a full-section jump
      // even on a tall viewport. Subsequent gestures during the lock are queued
      // (latest direction wins) below.
      lockTimer = setTimeout(() => {
        locked = false;
      }, 1100);
    };

    const overflowsViewport = (el) =>
      el.getBoundingClientRect().height > window.innerHeight + 2;

    const allowsInnerScroll = (el) =>
      !!el && (el.id === "faq" || el.tagName === "FOOTER");

    const onWheel = (e) => {
      if (!mq.matches) return;
      const els = targets();
      const cur = els[idx];
      // FAQ / footer: scroll within themselves until their edge is reached.
      if (cur && allowsInnerScroll(cur) && overflowsViewport(cur)) {
        const r = cur.getBoundingClientRect();
        const down = e.deltaY > 0;
        if (down && r.bottom > window.innerHeight + 2) return;
        if (!down && r.top < -2) return;
      }
      e.preventDefault();
      if (locked) return; // ignore further gestures until the jump settles
      if (Math.abs(e.deltaY) < 4) return;
      go(e.deltaY > 0 ? 1 : -1);
    };

    const onKey = (e) => {
      if (!mq.matches || locked) return;
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        e.preventDefault();
        go(1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        go(-1);
      }
    };

    let touchY = 0;
    const onTouchStart = (e) => {
      touchY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (!mq.matches || locked) return;
      const dy = touchY - e.changedTouches[0].clientY;
      const cur = targets()[idx];
      if (cur && allowsInnerScroll(cur) && overflowsViewport(cur)) {
        const r = cur.getBoundingClientRect();
        if (dy > 0 && r.bottom > window.innerHeight + 2) return;
        if (dy < 0 && r.top < -2) return;
      }
      if (Math.abs(dy) > 50) go(dy > 0 ? 1 : -1);
    };

    // If the user scrolls manually (anchor link, address bar, etc.) and we
    // aren't mid-jump, keep idx in sync so the next wheel gesture is correct.
    const onScroll = () => {
      if (locked) return;
      syncFromScroll();
    };

    syncFromScroll();
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(lockTimer);
    };
  }, []);

  return null;
}
